import { useEffect, useState } from 'react';
import { debugLogger } from '@/utils/debugLogger';
import { isValidPermission } from '@/constants/permissions';

export interface User {
  id?: string;
  user_id?: string;
  username?: string;
  name?: string;
  nickname?: string;
  email?: string;
  is_super_admin?: boolean;
  user_type?: 'admin' | 'customer' | 'supplier';
  groups?: Array<{ id: string; title: string }>;
  permissions?: string[]; // 用户拥有的权限代码数组
}

export const usePermission = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Load user from localStorage or API
    const loadUser = () => {
      debugLogger.log('👤 [usePermission] Loading user from localStorage...');
      try {
        const userStr = localStorage.getItem('user_info');
        debugLogger.log(`👤 [usePermission] user_info exists: ${!!userStr}`);
        if (userStr) {
          const parsed = JSON.parse(userStr);
          debugLogger.log(`👤 [usePermission] Parsed user:`, parsed);
          debugLogger.log(`👤 [usePermission] User type: ${parsed.user_type}`);
          debugLogger.log(`👤 [usePermission] Username: ${parsed.username}`);
          setUser(parsed);
        } else {
          debugLogger.log('⚠️ [usePermission] No user_info in localStorage');
        }
      } catch (error) {
        debugLogger.log(`❌ [usePermission] Failed to parse user_info:`, error);
        console.error('Failed to load user info:', error);
      } finally {
        setLoading(false);
        debugLogger.log('👤 [usePermission] Loading complete');
      }
    };

    loadUser();
  }, []);

  // ========== 基础角色判断 ==========
  const isSuperAdmin = (): boolean => {
    if (!user) return false;
    return user.is_super_admin === true;
  };

  const isAdmin = (): boolean => {
    if (!user) return false;
    return user.user_type === 'admin' || isSuperAdmin();
  };

  const isCustomer = (): boolean => {
    if (!user) return false;
    return user.user_type === 'customer';
  };

  const isSupplier = (): boolean => {
    if (!user) return false;
    return user.user_type === 'supplier';
  };

  // ========== 核心权限检查函数 ==========
  /**
   * 检查用户是否拥有指定权限
   * 超级管理员自动拥有所有权限
   */
  const hasPermission = (permissionCode: string | string[]): boolean => {
    if (!user) return false;

    // 超级管理员拥有所有权限
    if (isSuperAdmin()) return true;

    // 检查权限代码是否有效
    const codes = Array.isArray(permissionCode) ? permissionCode : [permissionCode];
    const validCodes = codes.filter(isValidPermission);
    if (validCodes.length === 0) return false;

    // 检查用户权限列表
    const userPermissions = user.permissions || [];
    return validCodes.some((code) => userPermissions.includes(code));
  };

  /**
   * 检查用户是否拥有所有指定权限
   */
  const hasAllPermissions = (permissionCodes: string[]): boolean => {
    if (!user) return false;
    if (isSuperAdmin()) return true;

    const userPermissions = user.permissions || [];
    return permissionCodes.every((code) => userPermissions.includes(code));
  };

  /**
   * 检查用户是否拥有任一指定权限
   */
  const hasAnyPermission = (permissionCodes: string[]): boolean => {
    if (!user) return false;
    if (isSuperAdmin()) return true;

    const userPermissions = user.permissions || [];
    return permissionCodes.some((code) => userPermissions.includes(code));
  };

  // ========== 向后兼容的权限检查函数（基于新的权限代码系统） ==========
  const canAccessSystemManagement = (): boolean => {
    return hasAnyPermission([
      'user:view',
      'role:view',
      'attachment:view',
      'setting:view',
    ]);
  };

  const canAccessUserList = (): boolean => {
    return hasPermission('user:view');
  };

  const canAccessRoles = (): boolean => {
    return hasPermission('role:view');
  };

  const canAccessPermissions = (): boolean => {
    return hasPermission('role:view');
  };

  const canAccessAttachments = (): boolean => {
    return hasPermission('attachment:view');
  };

  const canAccessApproval = (): boolean => {
    return isAdmin(); // 保持旧逻辑，后续可改为权限代码
  };

  const canAccessTickets = (): boolean => {
    return hasPermission('ticket:view');
  };

  const canAccessCustomerTickets = (): boolean => {
    return hasPermission('ticket:view') || isCustomer();
  };

  const canAccessSupplierTickets = (): boolean => {
    return hasPermission('ticket:view') || isSupplier();
  };

  const canAccessAdminTickets = (): boolean => {
    return hasPermission('ticket:view') && isAdmin();
  };

  const canModifyUser = (): boolean => {
    return hasAnyPermission(['user:create', 'user:update', 'user:delete']);
  };

  const canModifyRole = (): boolean => {
    return hasAnyPermission(['role:create', 'role:update', 'role:delete']);
  };

  const canModifyPermission = (): boolean => {
    return hasPermission('role:assign_permission');
  };

  const canAccessDrugManagement = (): boolean => {
    return isAdmin() || isSupplier(); // 保持旧逻辑
  };

  const canAccessAdminDrugs = (): boolean => {
    return isAdmin();
  };

  const canAccessDrugCategories = (): boolean => {
    return isAdmin();
  };

  const canAccessSupplierDrugs = (): boolean => {
    return isSupplier();
  };

  const canAccessDrugApproval = (): boolean => {
    return isAdmin();
  };

  return {
    user,
    loading,
    // 核心权限检查函数
    hasPermission,
    hasAllPermissions,
    hasAnyPermission,
    // 基础角色判断
    isSuperAdmin,
    isAdmin,
    isCustomer,
    isSupplier,
    // 向后兼容的权限检查函数
    canAccessSystemManagement,
    canAccessUserList,
    canAccessRoles,
    canAccessPermissions,
    canAccessAttachments,
    canAccessApproval,
    canAccessTickets,
    canAccessCustomerTickets,
    canAccessSupplierTickets,
    canAccessAdminTickets,
    canModifyUser,
    canModifyRole,
    canModifyPermission,
    canAccessDrugManagement,
    canAccessAdminDrugs,
    canAccessDrugCategories,
    canAccessSupplierDrugs,
    canAccessDrugApproval,
  };
};
